import TWEEN, { Tween } from '@tweenjs/tween.js';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const valueToY = (value: number) => 180 - value * 360;

const hash = uuidv4();

export interface LoadingIndicatorProps {
  duration?: number;
  error?: boolean;
  onAnimationDone?: (progress: number) => void;
  progress?: number; // [0-1]
  size?: number;
}

const LoadingIndicator = ({
  duration = 500,
  error = false,
  onAnimationDone,
  progress = 0.5,
  size = 128,
  ...props
}: LoadingIndicatorProps) => {
  const [animating, setAnimating] = useState(true);
  const [waveY, setWaveY] = useState(valueToY(0));
  const [waveX, setWaveX] = useState(0);
  const [reverseWaveX, setReverseWaveX] = useState(0);
  const [glowOpacity, setGlowOpacity] = useState(1);

  // Wave and glow animation
  useEffect(() => {
    new Tween({ x: -160, reverseX: 320 })
      .to({ x: 160, reverseX: 0 }, 2000)
      .repeat(Infinity)
      .onUpdate(tween => {
        setWaveX(tween.x);
        setReverseWaveX(tween.reverseX);
      })
      .start();

    new Tween({ opacity: 1 })
      .to({ opacity: 0.5 }, 1000)
      .yoyo(true)
      .repeat(Infinity)
      .onUpdate(tween => {
        setGlowOpacity(tween.opacity);
      })
      .start();

    const animate = (currentTime: number) => {
      TWEEN.update(currentTime);
      if (animating) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    return () => {
      setAnimating(false);
    };
  }, []);

  // Progress animation
  useEffect(() => {
    const start = waveY;
    const localEnd = valueToY(progress);
    new Tween({ y: start })
      .to({ y: localEnd }, duration)
      .onUpdate(tween => {
        setWaveY(tween.y);
      })
      .onComplete(tween => {
        onAnimationDone && onAnimationDone(progress);
        if (tween.y === valueToY(1)) {
          setAnimating(false);
        }
      })
      .start();
  }, [progress]);

  return (
    <svg width={size} height={size} viewBox="0 0 430 430" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g>
        <circle cx="215" cy="215" r="160" opacity="0.5" fill="#73B1FF" />
        <g filter={`url(#glowfilter-${hash})`} stroke={error ? '#DA3F1D' : '#4A90E2'} opacity={glowOpacity}>
          <circle cx="215" cy="215" r="191.22" strokeWidth="3.56039" />
        </g>
        <mask
          id={`circle-mask-${hash}`}
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="55"
          y="55"
          width="320"
          height="320"
        >
          <circle cx="215" cy="215" r="160" fill="#4B90E2" />
        </mask>
        {error ? (
          <g mask={`url(#circle-mask-${hash})`}>
            <rect x="55" y="215" width="320" height="215" fill="#EB5757" />
            <rect x="55" y="215" width="320" height="215" fill={`url(#front-wave-gradient-${hash})`} />
          </g>
        ) : (
          <>
            <mask id={`back-wave-${hash}`}>
              <path
                d="M-261.98 214.335C-261.98 214.335 -178.962 143.764 -100.471 214.335C-21.9808 284.907 58.0195 214.335 58.0195 214.335V397.309H-261.98V214.335Z M58.0195 214.335C58.0195 214.335 141.038 143.764 219.529 214.335C298.019 284.907 378.02 214.335 378.02 214.335V397.309H58.0195V214.335Z"
                fill="white"
                transform={`translate(${reverseWaveX} ${waveY})`}
              />
            </mask>
            <g mask={`url(#circle-mask-${hash})`} opacity="0.8">
              <g mask={`url(#back-wave-${hash})`}>
                <rect x="55" y="55" width="320" height="320" fill="#4A90E2" />
                <rect x="55" y="55" width="320" height="320" fill={`url(#back-wave-gradient-${hash})`} />
              </g>
            </g>

            <mask id={`front-wave-${hash}`}>
              <g transform={`translate(0, ${waveY})`}>
                <path
                  d="M-108.02 214.335C-108.02 214.335 -25.001 143.764 53.4896 214.335C131.98 284.907 211.98 214.335 211.98 214.335V397.309H-108.02V214.335Z M211.98 214.335C211.98 214.335 294.999 143.764 373.49 214.335C451.98 284.907 531.98 214.335 531.98 214.335V397.309H211.98V214.335Z"
                  fill="white"
                  transform={`translate(${waveX}, 0)`}
                />
                <rect x="55" y="396" width="320" height="160" fill="white" />
              </g>
            </mask>
            <g mask={`url(#circle-mask-${hash})`}>
              <g mask={`url(#front-wave-${hash})`}>
                <rect x="55" y="55" width="320" height="320" fill="#4A90E2" />
                <rect x="55.1758" y="55" width="320" height="320" fill={`url(#front-wave-gradient-${hash})`} />
              </g>
            </g>
          </>
        )}
      </g>
      <defs>
        <filter
          id={`glowfilter-${hash}`}
          x="0.637679"
          y="0.637679"
          width="428.725"
          height="428.725"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="7.12077" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_7614_90775" />
          <feOffset />
          <feGaussianBlur stdDeviation="7.12077" />
          <feColorMatrix
            type="matrix"
            values={
              error
                ? '0 0 0 0 0.921569 0 0 0 0 0.341176 0 0 0 0 0.341176 0 0 0 0.5 0'
                : '0 0 0 0 0.290196 0 0 0 0 0.564706 0 0 0 0 0.886275 0 0 0 0.5 0'
            }
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7614_90775" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7614_90775" result="shape" />
        </filter>
        <linearGradient
          id={`back-wave-gradient-${hash}`}
          x1="290.471"
          y1="215"
          x2="55.1758"
          y2="215"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop offset="1" stopOpacity="0.4" />
        </linearGradient>
        <linearGradient
          id={`front-wave-gradient-${hash}`}
          x1="375.176"
          y1="215"
          x2="145.565"
          y2="215"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0.4" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default LoadingIndicator;
